import React, { useRef, useState, useEffect, forwardRef } from "react";
import Slider from "react-slick";
import { useIsMobile, useLoadOnVisible } from "../../hooks";
import { motion } from "framer-motion";


/**
 * @returns {JSX.Element}
 * @constructor
 */


const AirInsights = ({ className = "", data }) => {
  const isMobile = useIsMobile(769);
  const sliderRef = useRef(null);
  const sliderWrapRef = useRef(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);
  const [slideDuration, setSlideDuration] = useState(data[0].duration);
  const [progress, setProgress] = useState(-100);
  const [slideInView, setSlideInView] = useState(false);
  const isSliderInView = useLoadOnVisible(sliderWrapRef, { threshold: 0.2 });

  useEffect(() => {
    if (slideInView) return;
    setSlideInView(isSliderInView);
  }, [isSliderInView]);

  useEffect(() => {
    setSlideDuration(isMobile ? data[nextIndex].duration : 0);
  }, [isMobile, nextIndex, data]);

  useEffect(() => {
    if (!slideInView) return;
    const currentVideo = document.querySelector(
      ".air-insights__slider .slick-current .air-insights__video"
    );

    if (!isMobile) return;

    const allVideos = document.querySelectorAll(
      ".air-insights__slider .air-insights__video"
    );
    allVideos.forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });

    setTimeout(() => {
      currentVideo?.play();
    }, 100);
  }, [currentVideoIndex, isMobile, slideInView, nextIndex]);

  useEffect(() => {
    if (slideDuration === 0) return;
    setProgress(-100);

    const interval = setInterval(() => {
      setProgress((prev) => (prev >= 0 ? -100 : prev + 1));
    }, slideDuration * 0.01);
    return () => clearInterval(interval);
  }, [nextIndex, slideDuration, isMobile, slideInView]);

  const settings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: slideDuration,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    centerMode: true,
    afterChange: (idx) => {
      setCurrentVideoIndex(idx);
    },
    beforeChange: (idx, nextIdx) => {
      setNextIndex(nextIdx);
      setSlideDuration(data[nextIdx].duration);
    },
  };
  return (
    <section className={`air-insights ${className}`}>
      <div className="air-insights__card">
        <div className="container">
          <div className="air-insights__header">
            <motion.h2
              className="air-insights__heading"
              initial={{ opacity: 0, y: 20 }}
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.02 }}
              variants={{
                visible: { opacity: 1, y: 0 },
                hidden: { opacity: 0, y: 20 },
              }}
            >
              She safeguards your home from the dangers you can’t see or smell.
            </motion.h2>
            <motion.p
              className="air-insights__short-intro"
              initial={{ opacity: 0, y: 20 }}
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.03 }}
              variants={{
                visible: { opacity: 1, y: 0 },
                hidden: { opacity: 0, y: 20 },
              }}
            >
              It’s impossible to know WTF is in your air—until now. Mila’s got 8
              smart sensors on the scene, <br /> and they don’t miss a thing.
            </motion.p>
          </div>
          {isMobile ? (
            <div className="air-insights__slider-wrapper" ref={sliderWrapRef}>
              {isSliderInView && (
                <Slider
                  className="air-insights__slider"
                  {...settings}
                  ref={sliderRef}
                  style={{ "--progress": `${progress}%` }}
                >
                  {data.map((item, index) => (
                    <Item {...item} key={index} isMobile={isMobile} />
                  ))}
                </Slider>
              )}
            </div>
          ) : (
            <div className="air-insights__grid">
              {data.map((item, index) => (
                <Item {...item} key={index} index={index} />
              ))}
            </div>
          )}
          <div className="air-insights__details footer-content">
            <h4>Fun Fact 🤓</h4>
            <p>
              86% of Mila owners say they know more about their home’s health
              within the first week of use.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Item = forwardRef(
  ({ videoUrl, title, content, index, isMobile, poster }, ref) => {
    const videoRef = useRef(null);
    const isInView = useLoadOnVisible(videoRef, { threshold: 0.2 });

    return (
      <motion.div
        className="air-insights__item"
        key={title}
        initial={{ opacity: 0 }}
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.01 * index }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        ref={videoRef}
      >
        <div className="air-insights__content">
          <h3 className="air-insights__title">{title}</h3>

          <div className="air-insights__text">
            <p>{content}</p>
          </div>
        </div>
        <div className="air-insights__media">
          <video
            src={videoUrl}
            className="air-insights__video"
            playsInline
            poster={poster}
            autoPlay={!isMobile}
            loop
            muted
            ref={ref}
          ></video>
        </div>
      </motion.div>
    );
  }
);

export default AirInsights;
